import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { globalSearchUserResponseSchema } from '../../../data-contract/global-search';

import type { GeneralFilter } from '../../../../../shared/source/data-contract/filter';
import type { GlobalSearchUserResponse } from '../../../data-contract/global-search';
import type { SortOption } from '../../pages/talent-directory/sort-option';
import type { UseQueryResult } from '@tanstack/react-query';
import type Maybe from 'true-myth/maybe';

export const useGlobalSearchDirectoryUsers = ({
    filterPayload,
    page,
    pageSize,
    searchTerm,
    sortOptions,
}: {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly page: number;
    readonly pageSize: number;
    readonly searchTerm: Maybe<string>;
    readonly sortOptions: readonly SortOption[];
}): UseQueryResult<GlobalSearchUserResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const hasSearchTerm = searchTerm.isJust;
    const pathname = hasSearchTerm ? '/global/search/users' : '/global/directory/users/filter';
    const operation = searchTerm.isJust ? 'get' : 'postJson';

    const mapSortField: (field: string) => string = (field) => {
        if (field === 'employeeName') {
            return 'fullName';
        }

        return field;
    };

    const sortModelAdapted = sortOptions.map(({ direction, field }) => ({
        direction,
        field: mapSortField(field),
    }));

    return useQuery({
        queryFn: async () => {
            const result = searchTerm.isJust
                ? await validatingCobrainerApiFetcher.get(globalSearchUserResponseSchema, {
                      pathname,
                      requestOptions: {
                          searchParams: {
                              page: String(page),
                              searchTerm: searchTerm.value,
                              size: String(pageSize),
                              sort: sortModelAdapted
                                  .map(({ direction, field }) => (direction === 'desc' ? `-${field}` : field))
                                  .join(','),
                          },
                      },
                  })
                : await validatingCobrainerApiFetcher.postJson(globalSearchUserResponseSchema, {
                      pathname,
                      payload: {
                          filter: filterPayload.unwrapOr(undefined),
                          paged: {
                              page: String(page),
                              size: String(pageSize),
                          },
                          sorted: {
                              fields: sortModelAdapted,
                          },
                      },
                  });

            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [pathname, operation, filterPayload, page, pageSize, sortOptions, searchTerm, sortModelAdapted],
    });
};
